<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Nuevo descanso médico</h1>
    </div>
    <div>
      <b-card no-body class="custom-card">
        <b-card-header class="custom-card-header d-flex align-items-center">
          <h3>Datos del descanso médico</h3>
        </b-card-header>
        <b-card-body class="custom-card-body p-4">

          <!-- ============================================================ -->
          <!-- SECCIÓN 1: Datos del descanso (siempre visible)              -->
          <!-- ============================================================ -->

          <!-- Empleado -->
          <div class="form-group">
            <label class="label">Empleado <span class="text-danger">*</span></label>
            <SearchableDropdown
              :options="employees"
              :oldValue="form.employeeId"
              placeholder="Buscar por nombre o número de documento"
              @selected="onEmployeeChange"
            />
            <div v-if="$v.form.employeeId.$error" class="form-error">
              El empleado es obligatorio
            </div>
          </div>

          <!-- Alerta sin contrato -->
          <div v-if="noActiveContract" class="alert alert-danger">
            <i class="ri-error-warning-line mr-2"></i>
            Este empleado no tiene contrato vigente.
          </div>

          <!-- Alerta documentación incompleta -->
          <div v-if="complianceError" class="alert alert-danger">
            <i class="ri-lock-line mr-2"></i>
            <strong>Documentación incompleta.</strong> No se puede registrar el descanso médico.
            Documentos faltantes: {{ complianceError.join(', ') }}
          </div>

          <!-- Resumen del empleado -->
          <div v-if="contractInfo && !noActiveContract" class="mb-4">
            <div class="row">
              <div class="col-md-4">
                <div class="stat-card p-3 border rounded text-center">
                  <div class="stat-label text-muted small">Empresa</div>
                  <div class="stat-value font-weight-bold">{{ contractInfo.companyName }}</div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="stat-card p-3 border rounded text-center">
                  <div class="stat-label text-muted small">Área</div>
                  <div class="stat-value font-weight-bold">{{ contractInfo.areaName }}</div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="stat-card p-3 border rounded text-center">
                  <div class="stat-label text-muted small">Descansos activos</div>
                  <div class="stat-value font-weight-bold text-primary">{{ summaryData.activeCount }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Tipo de descanso -->
          <div v-if="form.employeeId && !noActiveContract" class="form-group">
            <label class="label">Tipo de descanso <span class="text-danger">*</span></label>
            <FormSelect
              :items="typeOptions"
              v-model="form.leaveType"
              defaultOption="Seleccione tipo"
              @input="onTypeChange"
              :class="{ 'custom-form-control-error': $v.form.leaveType.$error }"
            />
            <div v-if="$v.form.leaveType.$error" class="form-error">
              El tipo de descanso es obligatorio
            </div>
          </div>

          <!-- Fechas -->
          <div v-if="form.leaveType" class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label class="label">Fecha de inicio <span class="text-danger">*</span></label>
                <FormDatepicker
                  v-model="form.startDate"
                  @input="onStartDateChange"
                />
                <div v-if="$v.form.startDate.$error" class="form-error">
                  La fecha de inicio es obligatoria
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="label">Fecha de fin <span class="text-danger">*</span></label>
                <FormDatepicker
                  v-model="form.endDate"
                  :disabled="isAutoEndDate"
                  @input="calculateDays"
                />
                <div v-if="$v.form.endDate.$error" class="form-error">
                  La fecha de fin es obligatoria
                </div>
              </div>
            </div>
            <div class="col-md-4 d-flex align-items-center">
              <div v-if="totalDays !== null" class="mt-3">
                <span class="badge badge-green">{{ totalDays }} días calendario</span>
                <div v-if="showDaySplit" class="text-muted small mt-1">
                  {{ employerDays }} días empleador / {{ subsidizedDays }} días EsSalud
                </div>
              </div>
            </div>
          </div>

          <!-- Notas informativas por tipo -->
          <div v-if="form.leaveType === 'MATERNIDAD' && form.startDate" class="alert alert-info py-2 small">
            <i class="ri-information-line mr-1"></i>
            Maternidad: 49 días prenatal + 49 días postnatal = 98 días calendario
          </div>
          <div v-if="form.leaveType === 'PATERNIDAD' && form.startDate" class="alert alert-info py-2 small">
            <i class="ri-information-line mr-1"></i>
            Paternidad: 10 días calendario según Ley 29409
          </div>

          <!-- ============================================================ -->
          <!-- SECCIÓN 2: Datos médicos (v-if tipo != PATERNIDAD)           -->
          <!-- ============================================================ -->
          <template v-if="showMedicalSection">
            <hr class="my-4">
            <h5 class="mb-3 font-weight-bold">Datos médicos</h5>

            <!-- N° CITT (solo ENFERMEDAD/ACCIDENTE) -->
            <div v-if="showCittField" class="form-group">
              <label class="label">Número CITT <span class="text-danger">*</span></label>
              <input
                type="text"
                v-model="form.cittNumber"
                class="custom-form-control"
                :class="{ 'custom-form-control-error': $v.form.cittNumber.$error }"
                placeholder="Número de CITT"
              />
              <div v-if="$v.form.cittNumber.$error" class="form-error">
                El número de CITT es obligatorio para este tipo de descanso
              </div>
            </div>

            <!-- Institución médica -->
            <div class="form-group">
              <label class="label">Institución médica</label>
              <input
                type="text"
                v-model="form.medicalInstitution"
                class="custom-form-control"
                placeholder="Nombre de la institución médica"
              />
            </div>

            <!-- Doctor -->
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="label">
                    Nombre del doctor
                    <span v-if="showCittField" class="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    v-model="form.doctorName"
                    class="custom-form-control"
                    :class="{ 'custom-form-control-error': $v.form.doctorName.$error }"
                    placeholder="Ej: LANCHIPA PICO HOGAR"
                  />
                  <div v-if="$v.form.doctorName.$error" class="form-error">
                    El nombre del doctor es obligatorio
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="label">
                    Especialidad
                    <span v-if="showCittField" class="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    v-model="form.doctorSpecialty"
                    class="custom-form-control"
                    :class="{ 'custom-form-control-error': $v.form.doctorSpecialty.$error }"
                    placeholder="Ej: Medicina Intensiva, Traumatología"
                  />
                  <div v-if="$v.form.doctorSpecialty.$error" class="form-error">
                    La especialidad es obligatoria
                  </div>
                </div>
              </div>
            </div>

            <!-- Diagnóstico (confidencial) -->
            <div class="form-group">
              <label class="label">
                Diagnóstico
                <span class="text-muted small ml-2"><i class="ri-lock-line"></i> Información confidencial</span>
              </label>
              <b-form-textarea
                v-model="form.diagnosis"
                rows="3"
                placeholder="Diagnóstico médico (confidencial, no visible en listas)"
              />
            </div>
          </template>

          <!-- ============================================================ -->
          <!-- SECCIÓN 3: Subsidio y trámite                                -->
          <!-- (v-if tipo != PATERNIDAD Y (totalDays > 20 O MATERNIDAD))    -->
          <!-- ============================================================ -->
          <template v-if="showSubsidySection">
            <hr class="my-4">
            <h5 class="mb-3 font-weight-bold">Subsidio y trámite EsSalud</h5>

            <div class="row">
              <!-- Estado del caso -->
              <div class="col-md-4">
                <div class="form-group">
                  <label class="label">Estado del caso</label>
                  <FormSelect
                    :items="caseStatusOptions"
                    v-model="form.caseStatus"
                    :disabled="form.leaveType === 'MATERNIDAD'"
                  />
                </div>
              </div>

              <!-- Tipo de seguro -->
              <div class="col-md-4">
                <div class="form-group">
                  <label class="label">Tipo de seguro</label>
                  <FormSelect
                    :items="insuranceTypeOptions"
                    v-model="form.insuranceType"
                    defaultOption="Seleccione"
                    :disabled="form.leaveType === 'MATERNIDAD'"
                  />
                </div>
              </div>

              <!-- Estado del trámite (v-if caseStatus = SUBSIDIO) -->
              <div class="col-md-4" v-if="showProcessFields">
                <div class="form-group">
                  <label class="label">Estado del trámite</label>
                  <FormSelect
                    :items="processStatusOptions"
                    v-model="form.processStatus"
                    defaultOption="Seleccione"
                  />
                </div>
              </div>
            </div>

            <!-- Montos (v-if caseStatus = SUBSIDIO) -->
            <div class="row" v-if="showProcessFields">
              <div class="col-md-4">
                <div class="form-group">
                  <label class="label">Pago por día (S/)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    v-model.number="form.dailyPayment"
                    class="custom-form-control"
                    placeholder="Monto diario del subsidio"
                  />
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group">
                  <label class="label">Monto pagado (S/)</label>
                  <input
                    type="text"
                    :value="calculatedAmountPaid !== null ? calculatedAmountPaid.toFixed(2) : '-'"
                    class="custom-form-control bg-light"
                    readonly
                  />
                  <small class="text-muted" v-if="subsidizedDays > 0">
                    {{ form.dailyPayment || 0 }} x {{ subsidizedDays }} días = S/ {{ calculatedAmountPaid !== null ? calculatedAmountPaid.toFixed(2) : '0.00' }}
                  </small>
                </div>
              </div>
              <div class="col-md-4" v-if="form.processStatus === 'COBRADO'">
                <div class="form-group">
                  <label class="label">Monto cobrado (S/)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    v-model.number="form.amountCollected"
                    class="custom-form-control"
                    placeholder="Monto reembolsado por EsSalud"
                  />
                </div>
              </div>
            </div>
          </template>

          <!-- ============================================================ -->
          <!-- SECCIÓN 4: Gestión (siempre visible cuando hay tipo)         -->
          <!-- ============================================================ -->
          <template v-if="form.leaveType">
            <hr class="my-4">
            <h5 class="mb-3 font-weight-bold">Gestión</h5>

            <!-- Encargado -->
            <div class="form-group">
              <label class="label">Encargado</label>
              <input
                type="text"
                v-model="form.personInCharge"
                class="custom-form-control"
                placeholder="Persona responsable del trámite"
              />
            </div>

            <!-- Archivo adjunto -->
            <div class="form-group">
              <label class="label">Archivo adjunto</label>
              <input
                type="file"
                ref="fileInput"
                accept=".pdf,.jpg,.jpeg,.png"
                @change="onFileChange"
                class="form-control-file"
              />
              <small class="text-muted">PDF, JPG o PNG. Máximo 10MB.</small>
            </div>

            <!-- Observaciones -->
            <div class="form-group">
              <label class="label">Observaciones</label>
              <b-form-textarea
                v-model="form.observations"
                rows="3"
                placeholder="Observaciones adicionales"
              />
            </div>
          </template>

          <!-- Botones -->
          <div class="form-group-buttons text-right mt-4">
            <router-link :to="{ name: 'medical-leave-list' }" class="button button-light mr-2">
              Cancelar
            </router-link>
            <button
              type="button"
              class="button button-primary"
              :disabled="!canSave"
              @click="save()"
            >
              Guardar
            </button>
          </div>

        </b-card-body>
      </b-card>
    </div>
  </div>
</template>

<script>
import { Alert, FormSelect, FormDatepicker, SearchableDropdown } from 'wize-admin'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { MedicalLeaveService, EmployeeService, VacationService } from '@/core/services'
import { Constants } from '@/core/utils'

const MATERNIDAD_DAYS = 98
const PATERNIDAD_DAYS = 10

export default {
  name: 'MedicalLeaveCreate',
  components: { FormSelect, FormDatepicker, SearchableDropdown },
  data() {
    return {
      employees: [],
      contractInfo: null,
      summaryData: { activeCount: 0 },
      noActiveContract: false,
      complianceError: null,
      selectedFile: null,
      form: {
        employeeId:         null,
        leaveType:          null,
        startDate:          null,
        endDate:            null,
        cittNumber:         '',
        medicalInstitution: '',
        diagnosis:          '',
        observations:       '',
        doctorName:         null,
        doctorSpecialty:    null,
        caseStatus:         'DESCANSO_MEDICO',
        processStatus:      null,
        insuranceType:      null,
        dailyPayment:       null,
        amountCollected:    null,
        personInCharge:     null,
      },
      typeOptions: Object.values(Constants.MedicalLeaveType).map(t => ({ key: t.key, label: t.label })),
      caseStatusOptions: Object.values(Constants.MedicalLeaveCaseStatus).map(t => ({ key: t.key, label: t.label })),
      processStatusOptions: Object.values(Constants.MedicalLeaveProcessStatus).map(t => ({ key: t.key, label: t.label })),
      insuranceTypeOptions: Object.values(Constants.MedicalLeaveInsuranceType).map(t => ({ key: t.key, label: t.label })),
    }
  },
  validations() {
    return {
      form: {
        employeeId: { required },
        leaveType:  { required },
        startDate:  { required },
        endDate:    { required },
        cittNumber: {
          required: requiredIf(() => this.showCittField),
        },
        doctorName: {
          required: requiredIf(() => this.showCittField),
        },
        doctorSpecialty: {
          required: requiredIf(() => this.showCittField),
        },
      },
    }
  },
  computed: {
    totalDays() {
      if (!this.form.startDate || !this.form.endDate) return null
      const start = new Date(this.form.startDate + 'T00:00:00')
      const end   = new Date(this.form.endDate   + 'T00:00:00')
      const diff  = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
      return diff > 0 ? diff : null
    },
    employerDays() {
      if (this.totalDays === null) return 0
      if (this.form.leaveType === 'MATERNIDAD') return 0
      if (this.form.leaveType === 'PATERNIDAD') return this.totalDays
      return Math.min(this.totalDays, 20)
    },
    subsidizedDays() {
      if (this.totalDays === null) return 0
      if (this.form.leaveType === 'MATERNIDAD') return this.totalDays
      if (this.form.leaveType === 'PATERNIDAD') return 0
      return Math.max(0, this.totalDays - 20)
    },
    calculatedAmountPaid() {
      if (!this.form.dailyPayment || !this.subsidizedDays) return null
      return this.form.dailyPayment * this.subsidizedDays
    },
    showMedicalSection() {
      return this.form.leaveType && this.form.leaveType !== 'PATERNIDAD'
    },
    showSubsidySection() {
      return this.form.leaveType
        && this.form.leaveType !== 'PATERNIDAD'
        && (this.totalDays > 20 || this.form.leaveType === 'MATERNIDAD')
    },
    showCittField() {
      return this.form.leaveType === 'ENFERMEDAD' || this.form.leaveType === 'ACCIDENTE'
    },
    showProcessFields() {
      return this.form.caseStatus === 'SUBSIDIO'
    },
    showDaySplit() {
      if (this.totalDays === null) return false
      return this.totalDays > 20 || this.form.leaveType === 'MATERNIDAD' || this.form.leaveType === 'PATERNIDAD'
    },
    isAutoEndDate() {
      return this.form.leaveType === 'MATERNIDAD' || this.form.leaveType === 'PATERNIDAD'
    },
    canSave() {
      return (
        this.form.employeeId &&
        !this.noActiveContract &&
        !this.complianceError &&
        this.form.leaveType &&
        this.form.startDate &&
        this.form.endDate &&
        this.totalDays > 0 &&
        (!this.showCittField || (this.form.cittNumber && this.form.cittNumber.trim())) &&
        (!this.showCittField || (this.form.doctorName && this.form.doctorName.trim())) &&
        (!this.showCittField || (this.form.doctorSpecialty && this.form.doctorSpecialty.trim()))
      )
    },
  },
  watch: {
    'form.leaveType'(newType) {
      if (newType === 'PATERNIDAD') {
        this.form.doctorName = null
        this.form.doctorSpecialty = null
        this.form.caseStatus = 'DESCANSO_MEDICO'
        this.form.processStatus = null
        this.form.insuranceType = null
        this.form.dailyPayment = null
        this.form.amountCollected = null
        this.form.cittNumber = ''
        this.form.diagnosis = ''
      } else if (newType === 'MATERNIDAD') {
        this.form.caseStatus = 'SUBSIDIO'
        this.form.insuranceType = 'ESSALUD'
        this.form.cittNumber = ''
      } else {
        // ENFERMEDAD / ACCIDENTE
        if (this.totalDays && this.totalDays > 20) {
          this.form.caseStatus = 'SUBSIDIO'
        } else {
          this.form.caseStatus = 'DESCANSO_MEDICO'
        }
      }
    },
    totalDays(newDays) {
      if (this.form.leaveType === 'MATERNIDAD' || this.form.leaveType === 'PATERNIDAD') return
      if (newDays && newDays > 20) {
        this.form.caseStatus = 'SUBSIDIO'
      } else {
        this.form.caseStatus = 'DESCANSO_MEDICO'
      }
    },
    'form.caseStatus'(newStatus) {
      if (newStatus !== 'SUBSIDIO') {
        this.form.processStatus = null
        this.form.dailyPayment = null
        this.form.amountCollected = null
      } else if (!this.form.processStatus) {
        this.form.processStatus = 'PENDIENTE'
      }
    },
  },
  methods: {
    async onEmployeeChange(employeeId) {
      this.form.employeeId = employeeId
      this.contractInfo    = null
      this.summaryData     = { activeCount: 0 }
      this.noActiveContract = false
      this.complianceError  = null
      this.form.leaveType  = null
      this.form.startDate  = null
      this.form.endDate    = null
      if (!employeeId) return
      try {
        this.$store.dispatch('app/loading', true)
        const balanceResp = await VacationService.getBalance(employeeId)
        const balance = balanceResp.payload
        if (!balance || !balance.contractId) {
          this.noActiveContract = true
          return
        }
        this.contractInfo = {
          companyName: balance.companyName || '-',
          areaName:    balance.laborRegime || '-',
        }
        try {
          const summaryResp = await MedicalLeaveService.getSummary(employeeId)
          this.summaryData = summaryResp.payload || { activeCount: 0 }
        } catch (e) {
          // non-critical
        }
        try {
          const compResp = await EmployeeService.getCompliance(employeeId)
          const p = compResp.payload
          this.complianceError = p.compliant ? null : p.missingDocuments
        } catch (e) { /* non-critical */ }
      } catch (e) {
        this.noActiveContract = true
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    onTypeChange() {
      this.form.startDate = null
      this.form.endDate   = null
    },
    onStartDateChange() {
      if (!this.form.startDate) {
        this.form.endDate = null
        return
      }
      if (this.form.leaveType === 'MATERNIDAD') {
        this.autoFillEndDate(MATERNIDAD_DAYS)
      } else if (this.form.leaveType === 'PATERNIDAD') {
        this.autoFillEndDate(PATERNIDAD_DAYS)
      }
    },
    autoFillEndDate(days) {
      const start = new Date(this.form.startDate + 'T00:00:00')
      start.setDate(start.getDate() + days - 1)
      this.form.endDate = start.toISOString().split('T')[0]
    },
    calculateDays() {
      // totalDays is a computed property, no manual action needed
    },
    onFileChange(e) {
      this.selectedFile = e.target.files[0] || null
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$error || !this.canSave) return
      try {
        this.$store.dispatch('app/loading', true)
        const fd = new FormData()
        fd.append('employeeId', this.form.employeeId)
        fd.append('leaveType',  this.form.leaveType)
        fd.append('startDate',  this.form.startDate)
        fd.append('endDate',    this.form.endDate)
        if (this.form.cittNumber && this.form.cittNumber.trim())
          fd.append('cittNumber', this.form.cittNumber.trim())
        if (this.form.medicalInstitution && this.form.medicalInstitution.trim())
          fd.append('medicalInstitution', this.form.medicalInstitution.trim())
        if (this.form.diagnosis && this.form.diagnosis.trim())
          fd.append('diagnosis', this.form.diagnosis.trim())
        if (this.form.observations && this.form.observations.trim())
          fd.append('observations', this.form.observations.trim())
        if (this.form.doctorName && this.form.doctorName.trim())
          fd.append('doctorName', this.form.doctorName.trim())
        if (this.form.doctorSpecialty && this.form.doctorSpecialty.trim())
          fd.append('doctorSpecialty', this.form.doctorSpecialty.trim())
        fd.append('caseStatus', this.form.caseStatus)
        if (this.form.processStatus)
          fd.append('processStatus', this.form.processStatus)
        if (this.form.insuranceType)
          fd.append('insuranceType', this.form.insuranceType)
        if (this.form.dailyPayment != null)
          fd.append('dailyPayment', this.form.dailyPayment)
        if (this.form.amountCollected != null)
          fd.append('amountCollected', this.form.amountCollected)
        if (this.form.personInCharge && this.form.personInCharge.trim())
          fd.append('personInCharge', this.form.personInCharge.trim())
        if (this.selectedFile)
          fd.append('file', this.selectedFile)
        await MedicalLeaveService.create(fd)
        Alert.success('Descanso médico registrado correctamente')
        this.$router.push({ name: 'medical-leave-list' })
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al registrar el descanso médico')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    if (this.$route.query.employeeId) {
      this.form.employeeId = Number(this.$route.query.employeeId)
      this.onEmployeeChange(this.form.employeeId)
    }
    try {
      const resp = await EmployeeService.listAll()
      const list = resp.payload || []
      this.employees = list.map(e => ({
        key:   e.id,
        label: e.description,
      }))
    } catch (e) {
      console.error(e)
    }
  },
}
</script>
